import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { IS_NATIVE } from '../utils/platform';

/**
 * "Sign in with Google", drawn by Google Identity Services.
 *
 * Google renders the button and its wording (Hebrew included) itself, so
 * this decides only whether there is one. There is none when the server has
 * no Google client configured (GET /auth/google/config); none in a native
 * build, as Google refuses OAuth inside an embedded WebView; and none on a
 * page that is not a secure context (plain http anywhere but localhost),
 * whose origin Google refuses too. A button there could only ever fail.
 *
 * The credential is a Google ID token, good for about an hour. It goes to
 * onCredential and nowhere else: not the URL, not storage, not the console.
 * The server verifies it; nothing here reads it.
 */

const GSI_SRC = 'https://accounts.google.com/gsi/client';

/* Google accepts a page served over plain http only from localhost, which
 * the browser counts as a secure context too. A phone reaching a laptop at
 * http://192.168.x.x -- what start_app.ps1 tells phones to use -- is neither:
 * Google refuses that origin, so a button there could only ever fail. */
const usableHere = () => typeof window === 'undefined' || window.isSecureContext !== false;

// One question per page load, however often the screen is opened. Any
// failure reads as "off": a sign-in screen without the button is what every
// server showed before it had one.
let configRequest = null;
function configuredClientId() {
  if (!configRequest) {
    configRequest = Promise.resolve()
      .then(() => api.get('/auth/google/config'))
      .then(res => (res?.data?.enabled && res.data.client_id) || null)
      .catch(() => null);
  }
  return configRequest;
}

let scriptRequest = null;
function loadIdentityServices() {
  if (window.google?.accounts?.id) return Promise.resolve(window.google.accounts.id);
  if (!scriptRequest) {
    scriptRequest = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = GSI_SRC;
      script.async = true;
      script.onload = () => {
        const gsi = window.google?.accounts?.id;
        if (gsi) resolve(gsi);
        else reject(new Error('Google Identity Services did not start'));
      };
      script.onerror = () => reject(new Error('Google Identity Services did not load'));
      document.head.appendChild(script);
    }).catch((err) => {
      // Not remembered: offline or a blocker now says nothing about the next
      // visit, which tries again with a fresh tag rather than the dead one.
      scriptRequest = null;
      document.querySelectorAll(`script[src="${GSI_SRC}"]`).forEach(s => s.remove());
      throw err;
    });
  }
  return scriptRequest;
}

// GIS wants initialize() once per page (it warns, and keeps only the last,
// when called again), but switching tabs mounts a new button. So it is
// initialised once, and its callback hands each credential to whichever
// button is on screen.
let initialisedFor = null;
let deliver = null;
function initialise(gsi, clientId) {
  if (initialisedFor === clientId) return;
  gsi.initialize({
    client_id: clientId,
    callback: ({ credential }) => { if (credential && deliver) deliver(credential); },
    ux_mode: 'popup',
    auto_select: false,
  });
  initialisedFor = clientId;
}

/**
 * Whether this page shows a "Sign in with Google" button at all: the same
 * question (and the same cached answer) as the button's own. The sign-in
 * page asks it after a wrong password, to point to Google only where there
 * is a Google button to point to.
 */
export function googleSignInAvailable() {
  if (IS_NATIVE || !usableHere()) return Promise.resolve(false);
  return configuredClientId().then(Boolean);
}

/** Tests only: forget the cached config, script and initialisation. */
export function resetGoogleSignInForTests() {
  configRequest = null;
  scriptRequest = null;
  initialisedFor = null;
  deliver = null;
}

export default function GoogleSignInButton({ mode = 'signin', onCredential, disabled = false }) {
  const { t, i18n } = useTranslation('login');
  const [clientId, setClientId] = useState(null);
  const [gsi, setGsi] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const holder = useRef(null);
  const latest = useRef({ onCredential, disabled });
  useEffect(() => { latest.current = { onCredential, disabled }; });

  useEffect(() => {
    const mine = (credential) => {
      const { onCredential: send, disabled: busy } = latest.current;
      // A second popup while the first credential is still with the server
      // would race it.
      if (!busy) send(credential);
    };
    deliver = mine;
    return () => { if (deliver === mine) deliver = null; };
  }, []);

  useEffect(() => {
    if (IS_NATIVE || !usableHere()) return undefined;
    let alive = true;
    configuredClientId().then((id) => { if (alive) setClientId(id); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!clientId) return undefined;
    let alive = true;
    loadIdentityServices().then((id) => {
      if (!alive) return;
      initialise(id, clientId);
      setGsi(id);
    }, () => { if (alive) setLoadFailed(true); });
    return () => { alive = false; };
  }, [clientId]);

  const hebrew = String(i18n?.language || '').startsWith('he');
  useEffect(() => {
    const el = holder.current;
    if (!gsi || !el) return;
    el.replaceChildren();
    gsi.renderButton(el, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      shape: 'pill',
      text: mode === 'register' ? 'signup_with' : 'signin_with',
      locale: hebrew ? 'he' : 'en',
      // Google's limit is 400 px; the card is narrower than that on a phone.
      width: Math.min(el.clientWidth || 400, 400),
    });
  }, [gsi, mode, hebrew]);

  if (IS_NATIVE || !usableHere() || !clientId) return null;

  return (
    <div data-testid="google-sign-in">
      <div style={styles.divider} aria-hidden="true">
        <span style={styles.rule} />
        <span>{t('google.or')}</span>
        <span style={styles.rule} />
      </div>
      {loadFailed ? (
        <p data-testid="google-load-failed" style={styles.note}>{t('google.loadFailed')}</p>
      ) : (
        <div ref={holder} aria-disabled={disabled || undefined} style={styles.holder(disabled)} />
      )}
    </div>
  );
}

const styles = {
  divider: {
    display: 'flex', alignItems: 'center', gap: 10, margin: '20px 0 16px',
    fontSize: 12, color: 'var(--c-text-faint-fg)',
  },
  rule: { flex: 1, height: 1, background: 'var(--c-bg-muted-2)' },
  holder: disabled => ({
    display: 'flex', justifyContent: 'center', minHeight: 44,
    opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? 'none' : undefined,
  }),
  note: { margin: 0, fontSize: 12, color: 'var(--c-text-muted-fg)', textAlign: 'center' },
};
